import type { FC } from "react";
import Lightbox from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";
import Counter from "yet-another-react-lightbox/plugins/counter";
import VideoPlugin from "yet-another-react-lightbox/plugins/video";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import type { Photo, Video } from "../../types";
import CloseIcon from "../../icons/close.svg?react";
import { useI18n } from "../../i18n";

import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/captions.css";
import "yet-another-react-lightbox/plugins/counter.css";
import styles from "./PhotoViewer.module.css";

type Props = {
  items: Array<Photo | Video>;
  open: boolean;
  index?: number;
  onClose: () => void;
  onIndexChange?: (index: number) => void;
};

export const PhotoViewer: FC<Props> = ({
  items,
  open,
  index,
  onClose,
  onIndexChange,
}) => {
  const i18n = useI18n();
  const getVideoMimeType = (src: string) => {
    const cleanSrc = src.split("?")[0]?.split("#")[0] ?? src;
    const ext = cleanSrc.slice(cleanSrc.lastIndexOf(".") + 1).toLowerCase();

    switch (ext) {
      case "webm":
        return "video/webm";
      case "ogg":
      case "ogv":
        return "video/ogg";
      case "mp4":
      default:
        return "video/mp4";
    }
  };

  const slides = items.map((item) => {
    if (item.type === "video") {
      return {
        type: "video" as const,
        poster: item.thumbnailSrc,
        description: item.caption,
        sources: [
          {
            src: item.src,
            type: getVideoMimeType(item.src),
          },
        ],
      };
    }

    return {
      src: item.src,
      width: item.width,
      height: item.height,
      alt: item.caption,
      description: item.caption,
    };
  });

  return (
    <Lightbox
      open={open}
      close={onClose}
      index={index}
      slides={slides}
      className={styles.root}
      plugins={[Zoom, VideoPlugin, Counter, Captions]}
      captions={{ descriptionTextAlign: "center", descriptionMaxLines: 2 }}
      counter={{
        separator: i18n.photoViewer.counterSeparator,
        container: { className: styles.counter },
      }}
      on={{
        view: ({ index: nextIndex }) => onIndexChange?.(nextIndex),
      }}
      render={{
        buttonZoom: () => null,
        buttonPrev: () => null,
        buttonNext: () => null,
        iconClose: () => <CloseIcon />,
      }}
    />
  );
};
